import {
  autoUpdate,
  FloatingPortal,
  flip,
  hide,
  limitShift,
  offset,
  shift,
  useFloating
} from '@floating-ui/react'
import clsx from 'clsx'
import { useEffect } from 'react'
import { useUpdateConfig } from '~/domains/configuration/mutations/updateConfig'
import { useConfig } from '~/domains/configuration/queries/config'
import type { ConfigName } from '~/domains/configuration/types'
import { themeClass } from '~/styles/theme.css'
import { Button, ButtonVariation } from '../Interactions/Button'
import { CloseTrigger } from '../Interactions/CloseTrigger/CloseTrigger'
import {
  inlineOnboarding,
  inlineOnboardingAnchorArrow,
  inlineOnboardingAnchorArrowEnd,
  inlineOnboardingAnchorArrowEndLeft,
  inlineOnboardingAnchorArrowEndRight,
  inlineOnboardingAnchorArrowLeft,
  inlineOnboardingAnchorArrowRight,
  inlineOnboardingDetails,
  inlineOnboardingFloating,
  inlineOnboardingFloatingEnd,
  inlineOnboardingFloatingHidden,
  inlineOnboardingFloatingLeft,
  inlineOnboardingFloatingRight,
  inlineOnboardingTitle
} from './InlineOnboarding.css'

type InlineOnboardingProps = {
  title: string
  details: string
  anchorRef: React.RefObject<HTMLElement | null>
  configName?: ConfigName
  show?: boolean
  // `end` anchors the bubble on the bottom of the element, for triggers close to the bottom of the screen
  align?: 'start' | 'end'
  onClose?: () => void
  onConfirm?: () => void
}

/**
 * Intentionally kept even when nothing imports it: this component is
 * reused each time a new feature ships to production to highlight it
 * to users, then the usage is removed once the feature is established.
 * Do not delete it as dead code.
 */
export const InlineOnboarding = ({
  title,
  details,
  anchorRef,
  configName,
  show = true,
  align = 'start',
  onClose,
  onConfirm
}: InlineOnboardingProps) => {
  const { refs, floatingStyles, placement, middlewareData } = useFloating({
    placement: `right-${align}`,
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: [`left-${align}`, `right-${align}`]
      }),
      shift({ padding: 8, limiter: limitShift() }),
      hide()
    ],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: anchorRef.current
    }
  })
  const { mutate: updateConfig } = useUpdateConfig(configName)
  const { data: configValue } = useConfig(configName)

  useEffect(() => {
    if (!anchorRef.current) return
    refs.setReference(anchorRef.current)
  }, [anchorRef.current, refs])

  const isLeftPlacement = placement.startsWith('left')
  const isEndAligned = align === 'end'

  if (configValue === true || configValue === undefined || !show) return null

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        className={clsx(
          themeClass,
          inlineOnboardingFloating,
          isEndAligned && inlineOnboardingFloatingEnd,
          middlewareData.hide?.referenceHidden &&
            inlineOnboardingFloatingHidden,
          isLeftPlacement
            ? inlineOnboardingFloatingLeft
            : inlineOnboardingFloatingRight
        )}
        style={floatingStyles}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={inlineOnboarding}>
          <span className={inlineOnboardingTitle}>{title}</span>
          <CloseTrigger
            onClose={() => {
              if (configName) {
                updateConfig({ value: true })
              }
              onClose?.()
            }}
          />

          <div className={inlineOnboardingDetails}>{details}</div>
          <Button
            variation={ButtonVariation.PrimaryInversed}
            onClick={() => {
              if (configName) {
                updateConfig({ value: true })
              }
              onConfirm?.()
            }}
            buttonSize="small"
            fullWidth
          >
            Compris
          </Button>
        </div>
        <svg
          className={clsx(
            inlineOnboardingAnchorArrow,
            isEndAligned
              ? [
                  inlineOnboardingAnchorArrowEnd,
                  isLeftPlacement
                    ? inlineOnboardingAnchorArrowEndLeft
                    : inlineOnboardingAnchorArrowEndRight
                ]
              : isLeftPlacement
                ? inlineOnboardingAnchorArrowLeft
                : inlineOnboardingAnchorArrowRight
          )}
          viewBox="0 0 58 22"
          aria-hidden="true"
        >
          <path
            d="M 1.6615302,7.039025 C 8.4512526,1.0834323 15.086539,0.65280231 20.134539,7.2752187 c 2.747343,3.6042093 5.389232,7.3176763 8.167796,8.7459683 2.885832,1.483433 5.919094,0.501844 9.371053,-5.627358 4.08625,-7.2554318 9.190859,-0.230138 16.573194,5.725454"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <path
            d="m 54.367086,10.519187 -0.0098,5.731115 -5.780701,0.05504"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </FloatingPortal>
  )
}
